'use client';
import Link from 'next/link';
import BannerSkeleton from './banner-skeleton';
import type { CampaignBanner } from '@/api/ahafood/campaign-banner';
import { SnapCarousel } from '@/components/carousel/snap-carousel';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';

type NewStoreBannerProps = {
  /** Danh sách banner type=FEATURED_BANNER (đã lọc + sort từ API mới). */
  banners: CampaignBanner[];
  isLoading?: boolean;
};

/**
 * Hero carousel — render từ CampaignBanner (type=FEATURED_BANNER), thay nguồn
 * Strapi cũ (AF-062). Ảnh dùng `image_url` (role-based §9). Click → `link` web.
 */
export default function NewStoreBanner({
  banners,
  isLoading,
}: NewStoreBannerProps) {
  const { user } = useAuthStore();

  if (isLoading) {
    return <BannerSkeleton />;
  }

  if (!banners || banners.length === 0) return null;

  const handleBannerClick = (banner: CampaignBanner) => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'click_banner',
      banner_id: banner._id,
      user_id: user?.cunstomerId,
    });
  };

  // Luôn dùng carousel (desktop: 2 ảnh/hàng qua basis-1/2). Bỏ nhánh 1 ảnh
  // full-width vì gây vỡ layout trên desktop rộng.
  return (
    <section className="bg-transparent pb-4 md:py-8">
      <div className="mx-auto w-full max-w-screen-xl md:px-6">
        <SnapCarousel autoplay contentClassName="ml-0 items-stretch">
          {banners.map((banner) => (
            <SnapCarousel.Item
              key={banner._id}
              className="basis-[88%] md:basis-1/2 pl-4 shrink-0"
            >
              <Link
                href={banner.link ?? ''}
                onClick={() => handleBannerClick(banner)}
                className="block size-full"
              >
                <div className="relative w-full aspect-[1000/443] overflow-hidden rounded-xl bg-white">
                  <img
                    src={banner.image_url}
                    alt={banner.title_vi_vn || 'Banner'}
                    className="size-full object-cover"
                  />
                </div>
              </Link>
            </SnapCarousel.Item>
          ))}
        </SnapCarousel>
      </div>
    </section>
  );
}
